import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Minus, Plus, ArrowLeft, ArrowRight, MapPin, Star, ChefHat, CopyCheck } from "lucide-react";
import api, { apiErr, resolveImg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const MAX_QTY = 50;

function QtyStepper({ id, value, onChange }) {
  return (
    <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
      <button data-testid={`qty-minus-${id}`} onClick={() => onChange(Math.max(0, value - 1))} disabled={value === 0}
        className="w-9 h-9 rounded-full bg-slate-100 grid place-items-center active:scale-90 transition-transform disabled:opacity-40"><Minus size={16} /></button>
      <span className="font-display text-2xl w-6 text-center" data-testid={`qty-value-${id}`}>{value}</span>
      <button data-testid={`qty-plus-${id}`} onClick={() => onChange(value + 1)}
        className="w-9 h-9 rounded-full bg-cc-red text-white grid place-items-center active:scale-90 transition-transform"><Plus size={16} /></button>
    </div>
  );
}

function PizzaCard({ p, qty, setQty }) {
  return (
    <div data-testid={`order-pizza-${p.id}`} className={`card-cocarde overflow-hidden border-2 transition-colors ${qty > 0 ? "border-cc-red" : "border-transparent"}`}>
      <div className="h-36 overflow-hidden relative">
        <img src={resolveImg(p.image_url)} alt={p.name} className="w-full h-full object-cover" />
        {p.is_specialty && <span className="absolute top-2 left-2 bg-cc-gold text-cc-navy text-xs font-bold rounded-full px-2 py-0.5 flex items-center gap-1"><Star size={11} /> Spécialité</span>}
        {p.custom && <span className="absolute top-2 left-2 bg-green-600 text-white text-xs font-bold rounded-full px-2 py-0.5 flex items-center gap-1"><ChefHat size={11} /> Fait maison</span>}
      </div>
      <div className="p-3">
        <div className="flex justify-between items-start gap-2">
          <div><h3 className="font-display text-lg cc-navy">{p.name}</h3><p className="text-slate-500 text-xs">{p.description}</p>{p.ingredients && <p data-testid={`order-ingredients-${p.id}`} className="text-cc-navy/70 text-xs mt-1 italic">{p.ingredients}</p>}</div>
          <span className="font-bold text-cc-red shrink-0">{p.price.toFixed(2)} €</span>
        </div>
        <div className="flex justify-end mt-3"><QtyStepper id={p.id} value={qty} onChange={setQty} /></div>
      </div>
    </div>
  );
}

function BoxPicker({ unit, index, boxes, value, onChange }) {
  return (
    <div data-testid={`order-unit-${index}`} className="py-4 border-b border-slate-100 last:border-0">
      <p className="font-bold cc-navy mb-2"><span className="text-cc-red">Pizza {index + 1}</span> · {unit.name}</p>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {boxes.map((b) => (
          <button key={b.id} onClick={() => onChange(b.id)} data-testid={`order-unit-${index}-box-${b.slug || b.id}`}
            className={`shrink-0 w-24 rounded-xl p-1.5 bg-slate-50 border-2 transition-all ${value === b.id ? "border-cc-red shadow-md" : "border-transparent hover:border-slate-200"}`}>
            <div className="h-16 grid place-items-center"><img src={resolveImg(b.image_url)} alt={b.name} className="max-h-16 object-contain" /></div>
            <p className="text-[11px] font-extrabold cc-navy mt-1 truncate">{b.name}</p>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function Order() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [pizzerias, setPizzerias] = useState([]);
  const [boxes, setBoxes] = useState([]);
  const [menu, setMenu] = useState([]);
  const [piz, setPiz] = useState(null);
  const [qtys, setQtys] = useState({});
  const [unitBoxes, setUnitBoxes] = useState({});
  const [info, setInfo] = useState({ customer_name: "", customer_phone: "", delivery_address: "", notes: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/pizzerias").then((r) => setPizzerias(r.data));
    api.get("/boxes").then((r) => setBoxes(r.data));
  }, []);
  useEffect(() => { if (user) setInfo((i) => ({ ...i, customer_name: i.customer_name || user.name })); }, [user]);
  useEffect(() => {
    if (!piz) return;
    setQtys({}); setUnitBoxes({});
    api.get(`/pizzerias/${piz}/menu`).then((r) => setMenu(r.data));
  }, [piz]);

  const set = (k) => (e) => setInfo({ ...info, [k]: e.target.value });
  const selPiz = pizzerias.find((p) => p.id === piz);
  const totalQty = Object.values(qtys).reduce((a, b) => a + b, 0);
  const setQty = (id) => (n) => {
    if (totalQty - (qtys[id] || 0) + n > MAX_QTY) { toast.error(`${MAX_QTY} pizzas maximum par commande`); return; }
    setQtys((q) => ({ ...q, [id]: n }));
  };
  const units = menu.flatMap((p) => Array.from({ length: qtys[p.id] || 0 }, (_, i) => ({ key: `${p.id}-${i}`, pizzaId: p.id, name: p.name, price: p.price })));
  const boxOf = (u) => unitBoxes[u.key] || boxes[0]?.id;
  const total = units.reduce((s, u) => s + u.price, 0).toFixed(2);
  const lines = Object.values(units.reduce((acc, u) => {
    const k = `${u.pizzaId}|${boxOf(u)}`;
    acc[k] = acc[k] || { pizza_id: u.pizzaId, box_id: boxOf(u), quantity: 0, name: u.name, price: u.price };
    acc[k].quantity += 1;
    return acc;
  }, {}));
  const applyAll = () => { const b = boxOf(units[0]); setUnitBoxes(Object.fromEntries(units.map((u) => [u.key, b]))); toast.success("Boîte appliquée à toutes les pizzas"); };

  const checkout = async () => {
    if (!user) { toast.error("Connectez-vous pour commander"); nav("/login"); return; }
    if (user.role !== "client") { toast.error("Seuls les comptes clients peuvent commander"); return; }
    if (!info.customer_name || !info.customer_phone || !info.delivery_address) { toast.error("Complétez vos informations de livraison"); return; }
    setBusy(true);
    try {
      const items = lines.map(({ pizza_id, box_id, quantity }) => ({ pizza_id, box_id, quantity }));
      const { data: order } = await api.post("/orders", { pizzeria_id: piz, items, ...info });
      const { data } = await api.post("/payments/checkout", { order_id: order.id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(apiErr(err)); setBusy(false); }
  };

  const labels = ["Pizzeria", "Pizzas", "Boîtes", "Livraison"];
  const Steps = () => (
    <div className="flex items-center justify-center gap-1 sm:gap-2 mb-8 flex-wrap">
      {labels.map((s, i) => (
        <div key={s} className="flex items-center gap-1 sm:gap-2">
          <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs sm:text-sm font-bold ${step >= i + 1 ? "bg-cc-red text-white" : "bg-slate-200 text-slate-500"}`}>
            {step > i + 1 ? <Check size={14} /> : <span>{i + 1}</span>} {s}
          </div>
          {i < labels.length - 1 && <div className={`w-4 sm:w-6 h-0.5 ${step > i + 1 ? "bg-cc-red" : "bg-slate-200"}`} />}
        </div>
      ))}
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <h1 className="font-display text-4xl sm:text-5xl cc-navy text-center">COMMANDER MA COCARDE</h1>
      <p className="text-center text-slate-500 mt-2">Choisissez votre pizzeria, vos pizzas, une boîte pour chacune, puis livraison.</p>
      <div className="mt-8"><Steps /></div>

      {step === 1 && (
        <div className="fade-up">
          <h2 className="font-display text-2xl cc-navy text-center mb-6">1. Choisissez votre pizzeria</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {pizzerias.map((p) => (
              <button key={p.id} onClick={() => setPiz(p.id)} data-testid={`order-pizzeria-${p.id}`}
                className={`text-left card-cocarde overflow-hidden border-2 ${piz === p.id ? "border-cc-red" : "border-transparent"}`}>
                <div className="flex">
                  <img src={p.image_url} alt={p.name} className="w-24 h-24 object-cover" />
                  <div className="p-3">
                    <h3 className="font-display text-lg cc-navy">{p.name}</h3>
                    <p className="text-slate-500 text-sm flex items-center gap-1"><MapPin size={13} /> {p.city}</p>
                    {piz === p.id && <span className="text-cc-red text-xs font-bold flex items-center gap-1 mt-0.5"><Check size={13} /> Sélectionnée</span>}
                  </div>
                </div>
              </button>
            ))}
          </div>
          <div className="text-center mt-8">
            <button data-testid="order-next-1" disabled={!piz} onClick={() => setStep(2)} className="btn-cocarde inline-flex items-center gap-2 disabled:opacity-50">Voir la carte <ArrowRight size={18} /></button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="fade-up">
          <h2 className="font-display text-2xl cc-navy text-center mb-1">2. La carte de {selPiz?.name}</h2>
          <p className="text-center text-slate-500 text-sm mb-6">Choisissez la quantité de chaque pizza.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {menu.map((p) => <PizzaCard key={p.id} p={p} qty={qtys[p.id] || 0} setQty={setQty(p.id)} />)}
          </div>
          <div className="flex justify-between items-center mt-8 gap-3">
            <button onClick={() => setStep(1)} className="btn-navy inline-flex items-center gap-2"><ArrowLeft size={18} /> Retour</button>
            <span data-testid="order-pizza-count" className="font-bold cc-navy text-sm sm:text-base">{totalQty} pizza{totalQty > 1 ? "s" : ""} · <span className="text-cc-red">{total} €</span></span>
            <button data-testid="order-next-2" disabled={totalQty === 0} onClick={() => setStep(3)} className="btn-cocarde inline-flex items-center gap-2 disabled:opacity-50">Continuer <ArrowRight size={18} /></button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="card-cocarde p-6 sm:p-8 fade-up">
          <h2 className="font-display text-2xl cc-navy text-center mb-1">3. Une boîte pour chaque pizza</h2>
          <p className="text-center text-slate-500 text-sm mb-4">Chaque pizza commandée est livrée dans le modèle de boîte de votre choix.</p>
          {units.length > 1 && (
            <div className="flex justify-end">
              <button data-testid="order-apply-all-boxes" onClick={applyAll} className="text-sm font-bold text-cc-blue inline-flex items-center gap-1 hover:text-cc-red"><CopyCheck size={15} /> Même boîte que la pizza 1 pour toutes</button>
            </div>
          )}
          {units.map((u, i) => <BoxPicker key={u.key} unit={u} index={i} boxes={boxes} value={boxOf(u)} onChange={(b) => setUnitBoxes((m) => ({ ...m, [u.key]: b }))} />)}
          <div className="flex justify-between mt-6">
            <button onClick={() => setStep(2)} className="btn-navy inline-flex items-center gap-2"><ArrowLeft size={18} /> Retour</button>
            <button data-testid="order-next-3" disabled={!boxes.length} onClick={() => setStep(4)} className="btn-cocarde inline-flex items-center gap-2 disabled:opacity-50">Continuer <ArrowRight size={18} /></button>
          </div>
        </div>
      )}

      {step === 4 && (
        <div className="grid md:grid-cols-2 gap-6 fade-up">
          <div className="card-cocarde p-6">
            <h2 className="font-display text-2xl cc-navy mb-4">4. Livraison</h2>
            <div className="space-y-3">
              <input data-testid="order-name" placeholder="Nom complet" value={info.customer_name} onChange={set("customer_name")} className="w-full border border-slate-300 rounded-xl px-4 py-3 focus:border-cc-red outline-none" />
              <input data-testid="order-phone" placeholder="Téléphone" value={info.customer_phone} onChange={set("customer_phone")} className="w-full border border-slate-300 rounded-xl px-4 py-3 focus:border-cc-red outline-none" />
              <textarea data-testid="order-address" placeholder="Adresse de livraison" value={info.delivery_address} onChange={set("delivery_address")} rows={2} className="w-full border border-slate-300 rounded-xl px-4 py-3 focus:border-cc-red outline-none" />
              <input data-testid="order-notes" placeholder="Note (facultatif)" value={info.notes} onChange={set("notes")} className="w-full border border-slate-300 rounded-xl px-4 py-3 focus:border-cc-red outline-none" />
            </div>
          </div>
          <div className="card-cocarde p-6 h-fit">
            <h2 className="font-display text-2xl cc-navy mb-4">Récapitulatif</h2>
            <div className="flex justify-between text-slate-600 py-1"><span>Pizzeria</span><span className="font-bold cc-navy">{selPiz?.name}</span></div>
            <div className="mt-2 space-y-1" data-testid="order-summary-lines">
              {lines.map((l) => (
                <div key={`${l.pizza_id}-${l.box_id}`} className="flex justify-between gap-2 text-sm text-slate-600">
                  <span><span className="font-bold cc-navy">{l.quantity}× {l.name}</span> · boîte {boxes.find((b) => b.id === l.box_id)?.name}</span>
                  <span className="font-semibold shrink-0">{(l.price * l.quantity).toFixed(2)} €</span>
                </div>
              ))}
            </div>
            <div className="flex justify-between items-center mt-3 pt-3 border-t border-slate-100">
              <span className="font-display text-xl cc-navy">TOTAL · {totalQty} pizza{totalQty > 1 ? "s" : ""}</span>
              <span className="font-display text-3xl text-cc-red" data-testid="order-total">{total} €</span>
            </div>
            <button data-testid="order-pay-btn" onClick={checkout} disabled={busy || totalQty === 0} className="btn-cocarde w-full mt-4 disabled:opacity-60">{busy ? "Redirection…" : "Payer & commander"}</button>
            <button onClick={() => setStep(3)} className="w-full mt-2 text-slate-500 font-semibold text-sm">Retour</button>
            {!user && <p className="text-xs text-center text-slate-400 mt-3">Vous devez être <Link to="/login" className="text-cc-red font-bold">connecté</Link> pour payer.</p>}
          </div>
        </div>
      )}
    </div>
  );
}
